/* @flow */

import React, { useState } from 'react';
import { connect } from 'react-redux';
import { get, keyBy, mapValues } from 'lodash/fp';
import round from 'lodash/round';
import styled from '@emotion/styled';
import { margin } from 'polished';
import colorUtils from 'color';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';

import { updatePlayers } from 'app/actions/challengeActions';
import { updateSettings } from 'app/actions/fantasyInsiderActions';
import VS from 'app/components/icons/VS';
import PlayerAvatar from 'app/components/challenge/PlayerAvatar';
import Button from 'app/components/commons/Button';
import Columns from 'app/components/commons/Columns';
import Column from 'app/components/commons/Column';

type Props = {
  games: Array<Object>,
  teams: Object,
  players: Object,
  onUpdatePlayers: (players: Object) => void,
  onUpdateSettings: (data: Object) => void,
  settings: Object
};

const Row = styled.div({
  display: 'flex'
});

const TeamAbbr = styled.div({
  width: 40
});

const StyledSlider = styled(Slider)(({ theme, scheme }) => ({
  ...margin(0, theme.spacing.default),
  height: 5,
  '&.rc-slider-disabled': {
    background: 'none',
    opacity: 0.4
  },
  '& .rc-slider-handle': {
    border: 'none',
    backgroundColor: 'transparent',
    fontFamily: 'Ionicons',
    '&::before': {
      position: 'absolute',
      top: '50%',
      left: '50%',
      content: '"\\f21b"',
      color: colorUtils(theme.color.primary)
        .lighten(0.5)
        .string(),
      fontSize: 32,
      transform: 'translate3d(-50%, -50%, 0)'
    },
    '&::after': {
      position: 'absolute',
      top: '50%',
      left: '50%',
      content: '"\\f21b"',
      color: theme.color.primary,
      fontSize: 15,
      transform: 'translate3d(-50%, -50%, 0)'
    }
  },
  '& .rc-slider-rail': {
    backgroundColor: scheme === 'light' ? theme.color.basis5 : theme.color.basis2
  },
  '& .rc-slider-track': {
    backgroundColor: scheme === 'light' ? theme.color.basis5 : theme.color.basis2
  },
  [theme.mediaQuery.desktopAndUp]: {
    '& .rc-slider-handle': {
      '&::before': {
        fontSize: 24
      },
      '&::after': {
        fontSize: 12
      }
    }
  }
}));

// type Props = {};

const FloorCeilingContent = ({
  games,
  teams,
  settings,
  onUpdatePlayers,
  onUpdateSettings,
  players
}: Props) => {
  const [globalDev, setGlobalDev] = useState(get('globalDev', settings) || 0);
  const [teamsDev, setTeamsDev] = useState(get('teamsDev', settings) || mapValues(() => 0, teams));
  const [canApplyGlobalDev, setCanApplyGlobalDev] = useState(false);
  const [canApplyTeamsDev, setCanApplyTeamsDev] = useState(false);

  const handleApplyTeamsDev = (teamsDevData: Object, sync: boolean = true) => {
    const playersWithScore = Object.values(players).map(p => {
      return {
        ...p,
        insiderScoreUpdated: Math.max(
          0,
          round(
            p.insiderScore +
              (get(p.teamId, teamsDevData || teamsDev) || 0) * (p.insiderStdDev || 0),
            1
          )
        )
      };
    });
    onUpdatePlayers(keyBy('idStr', playersWithScore));
    setCanApplyTeamsDev(false);
    setCanApplyGlobalDev(true);

    if (sync) {
      onUpdateSettings({ teamsDev: teamsDevData || teamsDev });
    }
  };

  const handleApplyGlobalDev = () => {
    const newTeamsDev = mapValues(() => globalDev, teams);
    setTeamsDev(newTeamsDev);
    setCanApplyGlobalDev(false);
    handleApplyTeamsDev(newTeamsDev, false);

    onUpdateSettings({ globalDev, teamsDev: newTeamsDev });
  };

  const handleChangeTeamDev = (teamId, val) => {
    setTeamsDev({ ...teamsDev, [teamId]: val });
    setCanApplyTeamsDev(true);
    setCanApplyGlobalDev(true);
  };

  const handleChangeGlobalDev = val => {
    setGlobalDev(val);
    setCanApplyGlobalDev(true);
  };

  return (
    <div>
      <h4>
        Use the below settings to set the projections for each team on the slate. A floor projection
        moves a player towards their lowest expected score; a ceiling projection moves a player
        towards their highest expected score.
      </h4>
      <h3>Set Global</h3>
      <Columns className="is-vcentered">
        <Column className="is-half">
          <Row>
            <span>Floor</span>
            <StyledSlider
              step={0.1}
              min={-0.5}
              max={0.5}
              value={globalDev}
              onChange={val => handleChangeGlobalDev(val)}
            />
            <span>Ceiling</span>
          </Row>
        </Column>
        <Column className="is-half">
          <Button type="primary" onClick={handleApplyGlobalDev} disabled={!canApplyGlobalDev}>
            Set for all teams
          </Button>
        </Column>
      </Columns>
      <h3>Set floor/ceiling for each team</h3>
      <div>
        {games.map(({ gameId, awayId, awayAbbr, homeAbbr, homeId }) => (
          <Columns key={gameId} className="is-vcentered">
            <Column className="is-narrow">
              <PlayerAvatar
                image={get(`${awayId}.imageURI`, teams)}
                fallbackText={awayAbbr}
                size="small"
              />
            </Column>
            <Column className="is-narrow">
              <TeamAbbr>{awayAbbr}</TeamAbbr>
            </Column>
            <Column className="is-4">
              <Row>
                <span>F</span>
                <StyledSlider
                  step={0.1}
                  min={-0.5}
                  max={0.5}
                  value={get(awayId, teamsDev) || 0}
                  onChange={val => handleChangeTeamDev(awayId, val)}
                />
                <span>C</span>
              </Row>
            </Column>
            <Column className="is-narrow">
              <Row style={{ ...margin(0, 10) }}>
                <VS />
              </Row>
            </Column>
            <Column className="is-narrow">
              <PlayerAvatar
                image={get(`${homeId}.imageURI`, teams)}
                fallbackText={awayAbbr}
                size="small"
              />
            </Column>
            <Column className="is-narrow">
              <TeamAbbr>{homeAbbr}</TeamAbbr>
            </Column>
            <Column className="is-4">
              <Row>
                <span>F</span>
                <StyledSlider
                  step={0.1}
                  min={-0.5}
                  max={0.5}
                  value={get(homeId, teamsDev) || 0}
                  onChange={val => handleChangeTeamDev(homeId, val)}
                />
                <span>C</span>
              </Row>
            </Column>
          </Columns>
        ))}
        <Columns className="is-centered">
          <Column className="is-1">
            <Button block type="primary" onClick={handleApplyTeamsDev} disabled={!canApplyTeamsDev}>
              Apply
            </Button>
          </Column>
        </Columns>
      </div>
    </div>
  );
};

function mapStateToProps(state: GlobalState) {
  return {
    players: state.challenge.players,
    games: state.challenge.games,
    teams: state.challenge.teams,
    settings: state.fantasyInsider.settings
  };
}
const actions = {
  onUpdatePlayers: updatePlayers,
  onUpdateSettings: updateSettings
};

export default connect(mapStateToProps, actions)(FloorCeilingContent);
